import type { Patient } from "fhir/r5";
import type { MessagingPatient } from "src-next/modules/crm-clinical/messaging";
import { mapPatientToMessagingPatient } from "./mapPatientToMessagingPatient";
import { mapPersonToMessagingPatient } from "./mapPersonToMessagingPatient";
import { getPatientCpfKey, getPersonCpfDigits } from "./ecommercePersonDisplay";

/** Destino da lista ecommerce: paciente, cliente cadastrado, ou os dois. */
export type EcommerceWhatsAppTarget = {
  patient?: Patient;
  person?: fhir5.Person;
};

function isPatientList(
  input: Patient[] | EcommerceWhatsAppTarget[],
): input is Patient[] {
  if (input.length === 0) return true;
  return (input[0] as Patient).resourceType === "Patient";
}

function resolveCpf(target: EcommerceWhatsAppTarget): string {
  if (target.person) return getPersonCpfDigits(target.person);
  if (target.patient) return getPatientCpfKey(target.patient);
  return "";
}

/**
 * Monta destinatários FluxZap.
 * Se o CPF tiver Person nesta org, envia para Person/{id} (não para Patient).
 * Um disparo por CPF.
 */
export function resolveEcommerceWhatsAppRecipients(
  input: Patient[] | EcommerceWhatsAppTarget[],
  personByCpf: Map<string, fhir5.Person>,
): MessagingPatient[] {
  const targets: EcommerceWhatsAppTarget[] = isPatientList(input)
    ? input.map((patient) => ({ patient }))
    : input;

  const byCpf = new Map<string, MessagingPatient>();
  const withoutCpf: MessagingPatient[] = [];

  for (const target of targets) {
    const cpf = resolveCpf(target);
    const person = target.person || (cpf ? personByCpf.get(cpf) : undefined);
    const mapped = person
      ? mapPersonToMessagingPatient(person)
      : target.patient
        ? mapPatientToMessagingPatient(target.patient)
        : null;
    if (!mapped) continue;

    if (!cpf) {
      withoutCpf.push(mapped);
      continue;
    }

    const previous = byCpf.get(cpf);
    const mappedIsPerson = Boolean(mapped.forReference?.startsWith("Person/"));
    const previousIsPerson = Boolean(
      previous?.forReference?.startsWith("Person/"),
    );
    if (!previous || (mappedIsPerson && !previousIsPerson)) {
      byCpf.set(cpf, mapped);
    }
  }

  return [...byCpf.values(), ...withoutCpf];
}

/** Task de questionário só em Patient — nunca Person/. */
export function resolveEcommerceTaskPatientReference(
  input: Patient[] | EcommerceWhatsAppTarget[],
  recipientCount: number,
): string | null {
  if (recipientCount !== 1) return null;

  const patient = isPatientList(input)
    ? input[0]
    : input[0]?.patient;
  if (patient?.id) return `Patient/${patient.id}`;
  return null;
}
